import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { products } from '../stripe-config';

interface SubscriptionStatusProps {
  language: string;
}

interface SubscriptionData {
  subscription_status: string;
  price_id: string | null;
  current_period_end: number | null;
}

const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({ language }) => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const content = {
    en: {
      title: "Your Subscription",
      noSubscription: "No active subscription",
      loading: "Loading subscription...",
      error: "Failed to load subscription",
      status: {
        active: "Active",
        canceled: "Canceled",
        past_due: "Past Due",
        incomplete: "Incomplete",
        trialing: "Trial",
        not_started: "Not Started"
      },
      renewsOn: "Renews on",
      plan: "Plan"
    },
    da: {
      title: "Dit Abonnement",
      noSubscription: "Intet aktivt abonnement",
      loading: "Indlæser abonnement...",
      error: "Kunne ikke indlæse abonnement",
      status: {
        active: "Aktiv",
        canceled: "Annulleret",
        past_due: "Forfalden",
        incomplete: "Ufuldstændig",
        trialing: "Prøveperiode",
        not_started: "Ikke startet"
      },
      renewsOn: "Fornyes den",
      plan: "Plan"
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('stripe_user_subscriptions')
        .select('*')
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
        setError('Failed to load subscription');
      } else {
        setSubscription(data);
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  const getProductName = (priceId: string | null) => {
    if (!priceId) return 'Unknown Plan';
    const product = products.find(p => p.priceId === priceId);
    return product?.name || 'Unknown Plan';
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString(language === 'da' ? 'da-DK' : 'en-US');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <p className="text-gray-600">{content[language as keyof typeof content].loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <p className="text-red-600">{content[language as keyof typeof content].error}</p>
      </div>
    );
  }

  if (!subscription || subscription.subscription_status === 'not_started') {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-2">{content[language as keyof typeof content].title}</h3>
        <p className="text-gray-600">{content[language as keyof typeof content].noSubscription}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold mb-4">{content[language as keyof typeof content].title}</h3>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">{content[language as keyof typeof content].plan}:</span>
          <span className="font-medium">{getProductName(subscription.price_id)}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Status:</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            subscription.subscription_status === 'active' 
              ? 'bg-green-100 text-green-800'
              : subscription.subscription_status === 'canceled'
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {content[language as keyof typeof content].status[subscription.subscription_status as keyof typeof content['en']['status']] || subscription.subscription_status}
          </span>
        </div>
        
        {subscription.current_period_end && subscription.subscription_status === 'active' && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600">{content[language as keyof typeof content].renewsOn}:</span>
            <span className="font-medium">{formatDate(subscription.current_period_end)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionStatus;